/* CareCardDialog — doctor's PDF customization sheet (iter71).
 *
 * Opens from the Treatment tab's "Customize & Download" button. Loads
 * the case's saved care_card overrides (or empty), lets the doctor type
 * patient-specific clinical advice in 4 free-form fields, saves on
 * submit, and triggers the PDF download immediately.
 *
 * Why all-in-one (vs. separate Save + Download buttons): the typical
 * flow is "type → download → hand it to the patient". Two-button friction
 * adds nothing — the Save happens on submit, and we re-download if the
 * doctor opens the dialog again.
 *
 * Fields (mirroring patient_care_pdf.py):
 *   clinical_notes   — general patient-specific clinical advice
 *   diet_lifestyle   — food/drink/lifestyle restrictions
 *   pain_management  — analgesic guidance
 *   emergency_advice — what to call about, after-hours instructions
 *
 * Each field is capped at 2000 chars server-side. Plain text only
 * (no markdown / HTML rendered — the PDF escapes everything).
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { FilePdf, FloppyDisk, Sparkle } from "@phosphor-icons/react";


/* Field definitions — single source of truth so the dialog + the PDF
   render stay in sync. `placeholder` is intentionally specific so doctors
   get realistic prompts, not generic ones. */
const FIELDS = [
    {
        key: "clinical_notes",
        label: "Notes for this patient",
        hint: "Patient-specific clinical advice (e.g. about attachments, IPR, areas to watch).",
        placeholder: "e.g. Watch for soreness on lower right molar — you have an attachment at 4.6. Use orthowax if needed for the first 3 days.",
    },
    {
        key: "diet_lifestyle",
        label: "Diet & lifestyle",
        hint: "Any food / drink / habit restrictions specific to this patient.",
        placeholder: "e.g. Avoid coffee, red wine and turmeric-heavy curry while attachments are on. Helps prevent staining.",
    },
    {
        key: "pain_management",
        label: "Pain management",
        hint: "How to handle discomfort in the first few days of a new tray.",
        placeholder: "e.g. Paracetamol 500mg if needed, max 3 doses per day, not beyond 3 days. Cold water rinses help with soreness.",
    },
    {
        key: "emergency_advice",
        label: "When to call us directly",
        hint: "Specific scenarios + after-hours instructions for THIS clinic.",
        placeholder: "e.g. Call my mobile +91 9000000000 directly if your upper attachment falls off — we'll re-bond same-day. After 9 pm: send a WhatsApp.",
    },
];


function downloadPdfBlob(blob, filename) {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
}


export default function CareCardDialog({ open, onOpenChange, caseId, patientName }) {
    const [form, setForm] = useState(() => Object.fromEntries(FIELDS.map((f) => [f.key, ""])));
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [originalUpdatedAt, setOriginalUpdatedAt] = useState(null);

    // Load saved overrides whenever dialog opens. We DO this on every open
    // (not once on mount) because the doctor might have edited the case in
    // another tab between two openings of this dialog.
    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        setLoading(true);
        api.get(`/cases/${caseId}/care-card`)
            .then((r) => {
                if (cancelled) return;
                const d = r.data || {};
                setForm({
                    clinical_notes: d.clinical_notes || "",
                    diet_lifestyle: d.diet_lifestyle || "",
                    pain_management: d.pain_management || "",
                    emergency_advice: d.emergency_advice || "",
                });
                setOriginalUpdatedAt(d.updated_at || null);
            })
            .catch((err) => {
                toast.error(err.response?.data?.detail || "Couldn't load care card");
            })
            .finally(() => !cancelled && setLoading(false));
        return () => { cancelled = true; };
    }, [open, caseId]);

    const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

    const submit = async ({ andDownload }) => {
        setSaving(true);
        try {
            // Save first — server returns the canonical updated payload.
            await api.put(`/cases/${caseId}/care-card`, form);
            toast.success("Care card customizations saved");
            if (andDownload) {
                // Then fetch the PDF. The endpoint reads the just-saved
                // care_card from Mongo so this round-trip is consistent.
                const res = await api.get(`/cases/${caseId}/care-card.pdf`, { responseType: "blob" });
                const blob = new Blob([res.data], { type: "application/pdf" });
                const slug = (patientName || "patient").toLowerCase().replace(/\s+/g, "-").slice(0, 40);
                downloadPdfBlob(blob, `invisique-care-card-${slug}.pdf`);
                toast.success("Care card downloaded — hand it to the patient.");
            }
            onOpenChange(false);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Couldn't save / generate PDF");
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="care-card-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FilePdf size={18} weight="duotone" className="text-rose-600" />
                        Customize care card for {patientName || "this patient"}
                    </DialogTitle>
                    <DialogDescription>
                        Add patient-specific advice. All sections are optional — only the ones you fill in
                        will appear on the printed PDF. Treatment switch schedule is auto-generated from
                        the case data.
                    </DialogDescription>
                </DialogHeader>

                {loading ? (
                    <div className="py-8 text-center text-sm text-muted-foreground">Loading saved notes…</div>
                ) : (
                    <>
                        <div className="rounded-xl bg-primary/5 border border-primary/20 p-3 text-xs text-foreground/80 flex items-start gap-2">
                            <Sparkle size={12} weight="fill" className="text-primary mt-0.5 shrink-0" />
                            <span>
                                The PDF also auto-includes a <strong>switch schedule</strong> (e.g.
                                "Aligner 2 on Mon, 04 Jun 2026"), the wearing & cleaning guide, and
                                your clinic's contact details.
                            </span>
                        </div>

                        <div className="space-y-5 mt-1">
                            {FIELDS.map((f) => (
                                <div key={f.key}>
                                    <Label htmlFor={`cc-${f.key}`} className="text-sm font-semibold">{f.label}</Label>
                                    <p className="text-[11px] text-muted-foreground leading-snug mt-0.5 mb-1.5">{f.hint}</p>
                                    <Textarea
                                        id={`cc-${f.key}`}
                                        value={form[f.key]}
                                        onChange={(e) => set(f.key, e.target.value)}
                                        rows={3}
                                        maxLength={2000}
                                        placeholder={f.placeholder}
                                        data-testid={`care-card-field-${f.key}`}
                                        className="text-sm leading-relaxed"
                                    />
                                    <div className="text-right text-[10px] text-muted-foreground tabular-nums mt-1">
                                        {form[f.key].length} / 2000
                                    </div>
                                </div>
                            ))}
                        </div>

                        {originalUpdatedAt && (
                            <div className="text-[11px] text-muted-foreground mt-2 italic">
                                Last edited {new Date(originalUpdatedAt).toLocaleString("en-IN")}
                            </div>
                        )}
                    </>
                )}

                <DialogFooter className="mt-3 gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
                    <Button
                        variant="outline"
                        onClick={() => submit({ andDownload: false })}
                        disabled={saving || loading}
                        data-testid="care-card-save-only"
                    >
                        <FloppyDisk size={13} weight="bold" className="mr-1" />
                        {saving ? "Saving…" : "Save only"}
                    </Button>
                    <Button
                        onClick={() => submit({ andDownload: true })}
                        disabled={saving || loading}
                        data-testid="care-card-save-and-download"
                    >
                        <FilePdf size={13} weight="bold" className="mr-1" />
                        {saving ? "Working…" : "Save & download PDF"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
